import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import AdminUPIStrategies from "@/pages/AdminUPIStrategies";
import AdminUPIWebhook from "@/pages/AdminUPIWebhook";

const STATUS_BADGE = {
  initiated: { color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Lucide.Clock },
  pending: { color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Lucide.Clock },
  success: { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Lucide.CheckCircle2 },
  failed: { color: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: Lucide.XCircle },
  cancelled: { color: "bg-slate-500/15 text-slate-400 border-slate-500/30", icon: Lucide.Ban },
};

function ReceiverModal({ open, onClose, onSubmit, initial }) {
  const [upi, setUpi] = useState("");
  const [nm, setNm] = useState("");
  const [active, setActive] = useState(false);
  useEffect(() => {
    setUpi(initial?.upi_id || "");
    setNm(initial?.receiver_name || "");
    setActive(initial?.is_active || false);
  }, [initial, open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <div className="bx-heading text-lg font-bold">{initial ? "Edit receiver" : "Add UPI receiver"}</div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-slate-400">UPI ID *</label>
            <input data-testid="upi-modal-id" value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="yourname@bankhandle" className="bx-input bx-mono mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Receiver name (optional)</label>
            <input data-testid="upi-modal-name" value={nm} onChange={(e) => setNm(e.target.value)} placeholder="BINAREX AND CO" className="bx-input mt-1" />
          </div>
          {!initial && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span className="text-slate-300">Set as active receiver immediately</span>
            </label>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="upi-modal-save" onClick={() => onSubmit({ upi_id: upi.trim(), receiver_name: nm.trim(), is_active: active })} className="bx-btn-primary">{initial ? "Save" : "Add"}</button>
        </div>
      </div>
    </div>
  );
}

function LogEditModal({ open, log, onClose, onSaved }) {
  const [status, setStatus] = useState("initiated");
  const [txn, setTxn] = useState("");
  const [bank, setBank] = useState("");
  const [holder, setHolder] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (log) {
      setStatus(log.payment_status || "initiated");
      setTxn(log.upi_transaction_id || "");
      setBank(log.sender_bank_name || "");
      setHolder(log.sender_account_holder || "");
      setNotes(log.notes || "");
    }
  }, [log]);
  if (!open || !log) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/upi/logs/${log.id}`, {
        payment_status: status,
        upi_transaction_id: txn,
        sender_bank_name: bank,
        sender_account_holder: holder,
        notes,
      });
      toast.success("Payment updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <div className="bx-heading text-lg font-bold">Update payment</div>
        <div className="text-xs text-slate-400 mt-1">
          {log.customer_name} · <span className="bx-mono">{log.customer_mobile}</span> · ₹{Number(log.amount).toLocaleString()}
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-slate-400">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="bx-input mt-1" data-testid="log-modal-status">
              <option value="initiated">initiated</option>
              <option value="pending">pending</option>
              <option value="success">success</option>
              <option value="failed">failed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400">UPI Transaction ID {status === "success" && <span className="text-rose-300">*</span>}</label>
            <input data-testid="log-modal-txn" value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="e.g. 412345678901" className="bx-input bx-mono mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Sender Bank A/c Name {status === "success" && <span className="text-rose-300">*</span>}</label>
            <input data-testid="log-modal-bank" value={bank} onChange={(e) => setBank(e.target.value)} placeholder="e.g. HDFC Bank — Rahul Sharma" className="bx-input mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Sender A/c Holder Name (optional)</label>
            <input value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Defaults to customer name" className="bx-input mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Internal notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bx-input resize-y mt-1" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="bx-btn-primary disabled:opacity-50" data-testid="log-modal-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceModal({ open, log, onClose, onSaved }) {
  const [company, setCompany] = useState("BINAREX AND CO");
  const [address, setAddress] = useState("");
  const [gstin, setGstin] = useState("");
  const [items, setItems] = useState([{ name: "", quantity: 1, unit_price: 0 }]);
  const [busy, setBusy] = useState(false);
  const [logos, setLogos] = useState([]);
  const [logoFileId, setLogoFileId] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/uploads?kind=brand_logo").then((r) => setLogos(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (log) {
      const c = log.invoice_company || {};
      setCompany(c.name || "BINAREX AND CO");
      setAddress(c.address || "");
      setGstin(c.gstin || "");
      setLogoFileId(c.logo_file_id || "");
      if (log.invoice_items?.length) setItems(log.invoice_items);
      else setItems([{ name: "Service Payment", quantity: 1, unit_price: Number(log.amount || 0) }]);
      setCustomerEmail(log.invoice_emailed_to || "");
      setEmailEnabled(false);
    }
  }, [log]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("brand", "invoice");
      fd.append("file", file);
      const r = await api.post("/admin/uploads/brand-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoFileId(r.data.id);
      const list = await api.get("/admin/uploads?kind=brand_logo");
      setLogos(list.data || []);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setLogoUploading(false); }
  };

  const addRow = () => setItems((it) => [...it, { name: "", quantity: 1, unit_price: 0 }]);
  const removeRow = (i) => setItems((it) => it.filter((_, j) => j !== i));
  const updateRow = (i, patch) => setItems((it) => it.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const subtotal = useMemo(() => items.reduce((s, r) => s + Number(r.quantity || 0) * Number(r.unit_price || 0), 0), [items]);

  if (!open || !log) return null;

  const generate = async () => {
    if (!company.trim() || !address.trim()) { toast.error("Company name and address are required"); return; }
    if (!items.length || items.some((r) => !r.name?.trim() || !r.quantity || r.unit_price < 0)) {
      toast.error("Fill every line item (name, qty, unit price)"); return;
    }
    if (emailEnabled && (!customerEmail.trim() || !customerEmail.includes("@"))) {
      toast.error("Please enter a valid customer email"); return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem("binarex_token");
      const r = await fetch(`${API}/admin/upi/logs/${log.id}/invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          company_name: company.trim(),
          company_address: address.trim(),
          company_gstin: gstin.trim() || null,
          company_logo_file_id: logoFileId || null,
          items: items.map((r) => ({
            name: r.name.trim(),
            quantity: Number(r.quantity),
            unit_price: Number(r.unit_price),
            total: Number(r.quantity) * Number(r.unit_price),
          })),
          invoice_no: log.invoice_no || null,
          email_to_customer: emailEnabled,
          customer_email: emailEnabled ? customerEmail.trim() : null,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `Failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = r.headers.get("Content-Disposition") || "";
      const m = disposition.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `Invoice-${log.customer_name || "customer"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(emailEnabled ? `Invoice downloaded & emailed to ${customerEmail}` : "Invoice downloaded");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message || "Failed to download"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto bx-scrollbar">
        <div className="flex items-start justify-between">
          <div>
            <div className="bx-heading text-lg font-bold">Generate invoice</div>
            <div className="text-xs text-slate-400 mt-1">
              For {log.customer_name} · <span className="bx-mono">{log.customer_mobile}</span> · ₹{Number(log.amount).toLocaleString()} · {log.upi_transaction_id ? <>Txn <span className="bx-mono text-purple-300">{log.upi_transaction_id}</span></> : <span className="text-amber-300">Missing transaction ID — edit payment first</span>}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Invoice branding</div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="invoice-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name *" className="bx-input col-span-2" />
            <textarea data-testid="invoice-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder="Address *" className="bx-input col-span-2 resize-y" />
            <input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="GSTIN (optional)" className="bx-input bx-mono text-xs col-span-2" />
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>Logo (renders in PDF header)</span>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={logoUploading} className="text-purple-300 hover:text-purple-200 text-[11px]" data-testid="invoice-logo-upload">
                {logoUploading ? "Uploading…" : "+ Upload new logo"}
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: "none" }} onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
            <div className="flex gap-2 flex-wrap items-center" data-testid="invoice-logo-strip">
              <button type="button" onClick={() => setLogoFileId("")} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${!logoFileId ? "bg-purple-500/30 text-purple-200 ring-1 ring-purple-400" : "bg-white/5 text-slate-400"}`}>
                None
              </button>
              {logos.map((l) => (
                <button key={l.id} type="button" onClick={() => setLogoFileId(l.id)} className={`p-1 rounded ${logoFileId === l.id ? "ring-2 ring-purple-400" : "ring-1 ring-white/10 hover:ring-white/30"}`} data-testid={`invoice-logo-${l.id.slice(0, 8)}`}>
                  <img src={`${API}${l.url}`} alt="logo" style={{ height: 24, background: "#fff", padding: "2px 4px", borderRadius: 3 }} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Line items</div>
            <button onClick={addRow} className="text-xs text-purple-300 hover:text-purple-200" data-testid="invoice-add-row"><Lucide.Plus size={11} className="inline" /> add row</button>
          </div>
          <div className="space-y-2">
            {items.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-row-${i}`}>
                <input value={r.name} onChange={(e) => updateRow(i, { name: e.target.value })} placeholder="Product / service name *" className="bx-input col-span-6" />
                <input type="number" min="1" step="any" value={r.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })} placeholder="Qty" className="bx-input col-span-2" />
                <input type="number" min="0" step="0.01" value={r.unit_price} onChange={(e) => updateRow(i, { unit_price: e.target.value })} placeholder="Unit ₹" className="bx-input col-span-2" />
                <div className="col-span-2 flex items-center justify-end gap-2 text-sm text-purple-300 font-mono">
                  ₹{(Number(r.quantity || 0) * Number(r.unit_price || 0)).toFixed(2)}
                  {items.length > 1 && (
                    <button onClick={() => removeRow(i)} className="text-rose-300 hover:text-rose-200"><Lucide.X size={13} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-sm">
            <span className="text-slate-400">Subtotal: </span>
            <span className="font-bold text-purple-300">₹{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            {Math.abs(subtotal - Number(log.amount)) > 0.01 && (
              <div className="text-[11px] text-amber-300 mt-1">⚠ Subtotal differs from paid amount (₹{Number(log.amount).toFixed(2)}) — the invoice will show "Amount Paid" as the actual paid value.</div>
            )}
          </div>
        </div>

        {/* Email-to-customer */}
        <div className="mt-5 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="invoice-email-toggle" checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)} className="mt-1" />
            <div>
              <div className="text-slate-200 font-semibold">Email invoice to customer</div>
              <div className="text-[11px] text-slate-500">Attaches the PDF and sends via your configured SMTP.</div>
            </div>
          </label>
          {emailEnabled && (
            <input
              data-testid="invoice-email"
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              className="bx-input mt-2 w-full text-sm"
            />
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="invoice-download" onClick={generate} disabled={busy} className="bx-btn-primary disabled:opacity-50">
            {busy ? <><Lucide.Loader2 className="animate-spin" size={14} /> Generating…</> : <>
              {emailEnabled ? <Lucide.Mail size={14} /> : <Lucide.Download size={14} />}
              {emailEnabled ? "Download & email PDF" : "Download invoice PDF"}
            </>}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkZipButton() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("binarex_token");
      const u = new URL(`${API}/admin/upi/invoices/bulk-zip`);
      if (from) u.searchParams.set("date_from", from);
      if (to) u.searchParams.set("date_to", to);
      const r = await fetch(u.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `Failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `binarex-invoices-${from}_to_${to}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
      setOpen(false);
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="upi-bulk-zip-open" onClick={() => setOpen(true)} className="bx-btn-ghost text-xs">
        <Lucide.Archive size={12} /> Bulk export
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
          <div className="bx-card p-6 max-w-sm w-full">
            <div className="bx-heading text-lg font-bold">Bulk-download invoices</div>
            <div className="text-xs text-slate-400 mt-1">All <strong>success + invoiced</strong> payments in the date range will be packaged into a ZIP.</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-400">From</label>
                <input data-testid="bulk-zip-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bx-input mt-1 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-slate-400">To</label>
                <input data-testid="bulk-zip-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bx-input mt-1 text-sm" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="bx-btn-ghost">Cancel</button>
              <button data-testid="bulk-zip-download" onClick={download} disabled={busy} className="bx-btn-primary disabled:opacity-50">
                {busy ? <><Lucide.Loader2 className="animate-spin" size={14} /> Building ZIP…</> : <><Lucide.Download size={14} /> Download ZIP</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PAY_LINK_STATUS_BADGE(s) {
  const map = {
    active: { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Lucide.Link2 },
    paid: { color: "bg-purple-500/15 text-purple-300 border-purple-500/30", icon: Lucide.CheckCircle2 },
    expired: { color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Lucide.Hourglass },
    revoked: { color: "bg-rose-500/15 text-rose-300 border-rose-500/30", icon: Lucide.Ban },
  };
  return map[s] || map.active;
}

function CreatePayLinkModal({ open, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [nameHint, setNameHint] = useState("");
  const [mobileHint, setMobileHint] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setAmount(""); setLabel(""); setNameHint(""); setMobileHint(""); }
  }, [open]);
  if (!open) return null;
  const create = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/upi/links", {
        amount: amt,
        label: label.trim() || null,
        customer_name_hint: nameHint.trim() || null,
        customer_mobile_hint: mobileHint.trim() || null,
      });
      toast.success("Pay link created");
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <div className="bx-heading text-lg font-bold">Create payment link</div>
        <div className="text-xs text-slate-400 mt-1">Generates a unique <span className="bx-mono text-purple-300">/pay/&lt;token&gt;</span> link · valid 24h · single-use.</div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-slate-400">Amount (INR) *</label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
              <input data-testid="paylink-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="1" step="0.01" placeholder="1499.00" className="bx-input pl-8" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Label (visible to customer)</label>
            <input data-testid="paylink-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder='e.g. "Logo design — Acme Co"' className="bx-input mt-1" maxLength={200} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Pre-fill name (opt.)</label>
              <input value={nameHint} onChange={(e) => setNameHint(e.target.value)} placeholder="Customer name" className="bx-input mt-1" maxLength={120} />
            </div>
            <div>
              <label className="text-xs text-slate-400">Pre-fill mobile (opt.)</label>
              <input value={mobileHint} onChange={(e) => setMobileHint(e.target.value)} placeholder="9XXXXXXXXX" className="bx-input mt-1" maxLength={20} />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="paylink-create-submit" onClick={create} disabled={busy} className="bx-btn-primary disabled:opacity-50">{busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.Plus size={14} />} Create link</button>
        </div>
      </div>
    </div>
  );
}

function EmailPayLinkModal({ link, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!link) return null;
  const send = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/upi/links/${link.id}/email`, { email: email.trim(), note: note.trim() || null });
      toast.success(`Pay link emailed to ${email}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-md w-full">
        <div className="bx-heading text-lg font-bold">Email pay link</div>
        <div className="text-xs text-slate-400 mt-1">Sends a branded payment email to the customer with a tap-to-pay button.</div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-slate-400">Customer email *</label>
            <input data-testid="paylink-email-recipient" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="customer@example.com" className="bx-input mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Personal note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder='e.g. "Thanks for the order — here is the payment link as discussed."' className="bx-input resize-y mt-1" maxLength={400} />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="paylink-email-send" onClick={send} disabled={busy} className="bx-btn-primary disabled:opacity-50">{busy ? <Lucide.Loader2 className="animate-spin" size={14} /> : <Lucide.Send size={14} />} Send email</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUPI() {
  const [tab, setTab] = useState("logs");
  const [receivers, setReceivers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payLinks, setPayLinks] = useState([]);
  const [createLinkOpen, setCreateLinkOpen] = useState(false);
  const [emailLinkFor, setEmailLinkFor] = useState(null);
  const [recModal, setRecModal] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [logEdit, setLogEdit] = useState(null);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [alerts, setAlerts] = useState({ upi_alert_email: "", upi_alert_enabled: true });
  const [alertBusy, setAlertBusy] = useState(false);

  const loadReceivers = () => api.get("/admin/upi/receivers").then((r) => setReceivers(r.data));
  const loadLogs = () => api.get("/admin/upi/logs").then((r) => setLogs(r.data));
  const loadAlerts = () => api.get("/admin/upi/alerts").then((r) => setAlerts(r.data));
  const loadPayLinks = () => api.get("/admin/upi/links").then((r) => setPayLinks(r.data)).catch(() => setPayLinks([]));
  useEffect(() => { loadReceivers(); loadLogs(); loadAlerts(); loadPayLinks(); }, []);

  const saveAlerts = async () => {
    setAlertBusy(true);
    try {
      await api.put("/admin/upi/alerts", alerts);
      toast.success("Alerts updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAlertBusy(false); }
  };
  const sendTestAlert = async () => {
    try {
      await api.post("/admin/upi/alerts/test");
      toast.success(`Test alert sent to ${alerts.upi_alert_email || "(SMTP default)"} — check your inbox.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const active = useMemo(() => receivers.find((r) => r.is_active), [receivers]);
  const counts = useMemo(() => {
    const c = { initiated: 0, pending: 0, success: 0, failed: 0, cancelled: 0, total_inr: 0 };
    for (const l of logs) {
      c[l.payment_status] = (c[l.payment_status] || 0) + 1;
      if (l.payment_status === "success") c.total_inr += Number(l.amount || 0);
    }
    return c;
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((l) => {
      if (statusFilter && l.payment_status !== statusFilter) return false;
      if (q) {
        const hay = `${l.customer_name} ${l.customer_mobile} ${l.upi_transaction_id || ""} ${l.sender_bank_name || ""} ${l.merchant_reference_id || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [logs, statusFilter, q]);

  const createOrUpdateReceiver = async ({ upi_id, receiver_name, is_active }) => {
    try {
      if (editingRec) await api.patch(`/admin/upi/receivers/${editingRec.id}`, { upi_id, receiver_name });
      else await api.post("/admin/upi/receivers", { upi_id, receiver_name, is_active });
      toast.success("Saved");
      setRecModal(false); setEditingRec(null);
      loadReceivers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const activate = async (id) => { try { await api.post(`/admin/upi/receivers/${id}/activate`); loadReceivers(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const del = async (rec) => { if (!window.confirm(`Delete ${rec.upi_id}?`)) return; try { const r = await api.delete(`/admin/upi/receivers/${rec.id}`); toast.success(r.data?.promoted ? "Deleted (auto-promoted)" : "Deleted"); loadReceivers(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">UPI Pay Link</h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage receiver UPI IDs and verify customer payments. Public page: <span className="bx-mono text-purple-300">binarex.co/UPI</span>.
          </p>
        </div>
        {tab === "receivers" && (
          <button data-testid="upi-add-receiver" onClick={() => { setEditingRec(null); setRecModal(true); }} className="bx-btn-primary text-sm">
            <Lucide.Plus size={14} /> Add UPI ID
          </button>
        )}
        {tab === "links" && (
          <button data-testid="paylink-create-open" onClick={() => setCreateLinkOpen(true)} className="bx-btn-primary text-sm">
            <Lucide.Link2 size={14} /> Create pay link
          </button>
        )}
      </div>

      <div className="bx-card p-4 mt-5 flex items-center justify-between" data-testid="upi-active-banner">
        <div className="flex items-center gap-3">
          <Lucide.BadgeCheck size={22} className="text-emerald-300" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Currently active receiver</div>
            <div className="mt-0.5 text-sm">
              {active ? (
                <><span className="bx-mono text-purple-300">{active.upi_id}</span>{active.receiver_name && <span className="text-slate-400"> · {active.receiver_name}</span>}</>
              ) : (
                <span className="text-amber-300">No active receiver — UPI page will show an error.</span>
              )}
            </div>
          </div>
        </div>
        <a href="/UPI" target="_blank" rel="noopener noreferrer" className="bx-btn-ghost text-xs"><Lucide.ExternalLink size={12} /> Open /UPI</a>
      </div>

      <QuickSetupWizard
        receivers={receivers}
        onJumpTab={setTab}
        onAddReceiver={() => { setEditingRec(null); setRecModal(true); }}
        onCreateLink={() => setCreateLinkOpen(true)}
      />

      {/* Real-time alerts */}
      <div className="bx-card p-4 mt-3" data-testid="upi-alerts-card">
        <div className="flex items-center gap-3">
          <Lucide.Bell size={18} className={alerts.upi_alert_enabled ? "text-purple-300" : "text-slate-500"} />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Real-time payment alerts</div>
            <div className="mt-0.5 text-xs text-slate-400">An email is sent the instant a customer taps PAY NOW (throttled to one per 60s per mobile).</div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              data-testid="upi-alerts-enabled"
              checked={!!alerts.upi_alert_enabled}
              onChange={(e) => setAlerts({ ...alerts, upi_alert_enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            data-testid="upi-alerts-email"
            value={alerts.upi_alert_email || ""}
            onChange={(e) => setAlerts({ ...alerts, upi_alert_email: e.target.value })}
            placeholder="Notification email (defaults to SMTP sender)"
            className="bx-input flex-1 min-w-[260px] text-sm"
          />
          <button data-testid="upi-alerts-save" onClick={saveAlerts} disabled={alertBusy} className="bx-btn-primary text-xs disabled:opacity-50">
            {alertBusy ? <Lucide.Loader2 className="animate-spin" size={12} /> : <Lucide.Save size={12} />} Save
          </button>
          <button data-testid="upi-alerts-test" onClick={sendTestAlert} className="bx-btn-ghost text-xs"><Lucide.Send size={12} /> Send test</button>
        </div>
      </div>

      <ExpiringSoonWidget />

      <div className="flex items-center gap-2 mt-6 text-sm">
        <button onClick={() => setTab("logs")} className={`bx-chip ${tab === "logs" ? "ring-1 ring-purple-400" : ""}`} data-testid="upi-tab-logs">
          Payment logs · {logs.length}
        </button>
        <button onClick={() => setTab("receivers")} className={`bx-chip ${tab === "receivers" ? "ring-1 ring-purple-400" : ""}`} data-testid="upi-tab-receivers">
          Receivers · {receivers.length}
        </button>
        <button onClick={() => setTab("links")} className={`bx-chip ${tab === "links" ? "ring-1 ring-purple-400" : ""}`} data-testid="upi-tab-links">
          Pay links · {payLinks.length}
        </button>
        <button onClick={() => setTab("strategies")} className={`bx-chip ${tab === "strategies" ? "ring-1 ring-purple-400" : ""}`} data-testid="upi-tab-strategies">
          Strategies
        </button>
        <button onClick={() => setTab("webhook")} className={`bx-chip ${tab === "webhook" ? "ring-1 ring-purple-400" : ""}`} data-testid="upi-tab-webhook">
          Auto-confirm
        </button>
        {tab === "logs" && (
          <div className="ml-auto text-xs text-slate-400 flex items-center gap-3">
            <span>Collected: <span className="text-emerald-300 font-bold">₹{counts.total_inr.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
            <div className="relative">
              <Lucide.Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / mobile / txn…" className="bx-input pl-7 py-1.5 w-56 text-xs" />
            </div>
          </div>
        )}
      </div>

      {tab === "logs" && (
        <>
          {/* Status filter chips */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {[
              { v: "", label: "All", count: logs.length, color: "text-slate-200" },
              { v: "success", label: "Success", count: counts.success, color: "text-emerald-300" },
              { v: "initiated", label: "Pending", count: (counts.initiated || 0) + (counts.pending || 0), color: "text-amber-300" },
              { v: "failed", label: "Failed", count: counts.failed, color: "text-rose-300" },
              { v: "cancelled", label: "Cancelled", count: counts.cancelled, color: "text-slate-400" },
            ].map((f) => (
              <button
                key={f.v || "all"}
                onClick={() => setStatusFilter(f.v)}
                data-testid={`upi-filter-${f.v || "all"}`}
                className={`bx-chip text-xs ${statusFilter === f.v ? "ring-1 ring-purple-400" : ""}`}
              >
                <span className={f.color}>{f.label}</span> · {f.count}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <BulkZipButton />
            </div>
          </div>

          <div className="bx-card mt-4 overflow-x-auto" data-testid="upi-logs-table">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Mobile</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3" title="Merchant reference id — embedded in the UPI transaction note, used by auto-confirm">Ref</th>
                  <th className="text-left p-3">Sender Bank A/c</th>
                  <th className="text-left p-3">UPI Txn ID</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((l) => {
                  const meta = STATUS_BADGE[l.payment_status] || STATUS_BADGE.initiated;
                  const Icon = meta.icon;
                  return (
                    <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`upi-log-${l.id.slice(0, 8)}`}>
                      <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-3">{l.customer_name}</td>
                      <td className="p-3 bx-mono text-xs text-slate-400">{l.customer_mobile}</td>
                      <td className="p-3 text-right font-bold text-purple-300">₹{Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.color}`}>
                          <Icon size={10} /> {l.payment_status}
                        </span>
                      </td>
                      <td className="p-3 bx-mono text-[11px] text-purple-300/90" data-testid={`upi-log-ref-${l.id.slice(0, 8)}`}>{l.merchant_reference_id || <span className="text-slate-600">—</span>}</td>
                      <td className="p-3 text-xs text-slate-300 max-w-[180px] truncate">{l.sender_bank_name || <span className="text-slate-600">—</span>}</td>
                      <td className="p-3 bx-mono text-xs text-purple-300/90">{l.upi_transaction_id || <span className="text-slate-600">—</span>}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button data-testid={`upi-log-edit-${l.id.slice(0, 8)}`} onClick={() => setLogEdit(l)} className="bx-btn-ghost text-xs" title="Update status / txn"><Lucide.Pencil size={12} /></button>
                        {l.payment_status === "success" && (
                          <button data-testid={`upi-log-invoice-${l.id.slice(0, 8)}`} onClick={() => setInvoiceFor(l)} className="bx-btn-ghost text-xs ml-1 text-purple-300" title="Generate invoice"><Lucide.FileText size={12} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!filteredLogs.length && <tr><td colSpan={9} className="p-12 text-center text-slate-500 text-sm">No payments {statusFilter ? `with status "${statusFilter}"` : "yet"}.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "receivers" && (
        <div className="bx-card mt-4 overflow-x-auto" data-testid="upi-receivers-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left p-3">UPI ID</th>
                <th className="text-left p-3">Receiver name</th>
                <th className="text-center p-3">Active</th>
                <th className="text-left p-3">Added</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {receivers.map((r) => (
                <tr key={r.id} className={`border-t border-white/5 ${r.is_active ? "bg-purple-500/[0.04]" : ""}`} data-testid={`upi-receiver-row-${r.id.slice(0, 8)}`}>
                  <td className="p-3 bx-mono text-purple-300">{r.upi_id}</td>
                  <td className="p-3 text-slate-300">{r.receiver_name || "—"}</td>
                  <td className="p-3 text-center">
                    {r.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold"><Lucide.BadgeCheck size={11} /> Active</span>
                    ) : (
                      <button data-testid={`upi-receiver-activate-${r.id.slice(0, 8)}`} onClick={() => activate(r.id)} className="text-xs text-slate-400 hover:text-purple-300 underline">Make active</button>
                    )}
                  </td>
                  <td className="p-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`upi-receiver-edit-${r.id.slice(0, 8)}`} onClick={() => { setEditingRec(r); setRecModal(true); }} className="bx-btn-ghost text-xs"><Lucide.Pencil size={12} /></button>
                    <button data-testid={`upi-receiver-delete-${r.id.slice(0, 8)}`} onClick={() => del(r)} className="bx-btn-ghost text-xs text-rose-300 ml-1"><Lucide.Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
              {!receivers.length && <tr><td colSpan={5} className="p-12 text-center text-slate-500 text-sm">No UPI receivers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "links" && (
        <PayLinksPanel
          links={payLinks}
          onReload={loadPayLinks}
          onOpenEmail={(l) => setEmailLinkFor(l)}
        />
      )}

      {tab === "strategies" && (
        <div className="mt-4">
          <AdminUPIStrategies receivers={receivers} />
        </div>
      )}

      {tab === "webhook" && <AdminUPIWebhook />}

      <ReceiverModal open={recModal} initial={editingRec} onClose={() => { setRecModal(false); setEditingRec(null); }} onSubmit={createOrUpdateReceiver} />
      <LogEditModal open={!!logEdit} log={logEdit} onClose={() => setLogEdit(null)} onSaved={loadLogs} />
      <InvoiceModal open={!!invoiceFor} log={invoiceFor} onClose={() => setInvoiceFor(null)} onSaved={loadLogs} />
      <CreatePayLinkModal open={createLinkOpen} onClose={() => setCreateLinkOpen(false)} onCreated={() => loadPayLinks()} />
      <EmailPayLinkModal link={emailLinkFor} onClose={() => setEmailLinkFor(null)} onSent={loadPayLinks} />
    </AdminLayout>
  );
}

function QuickSetupWizard({ receivers, onJumpTab, onAddReceiver, onCreateLink }) {
  const [strategyCount, setStrategyCount] = useState(null);
  const [linkCount, setLinkCount] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/admin/upi/strategies").then((r) => setStrategyCount(r.data.length)).catch(() => setStrategyCount(0));
    api.get("/admin/upi/links?limit=1").then((r) => setLinkCount(r.data.length)).catch(() => setLinkCount(0));
  }, [receivers.length]);

  if (strategyCount === null || linkCount === null) return null;

  const hasReceiver = receivers.length > 0;
  const hasStrategy = strategyCount > 0;
  const hasLink = linkCount > 0;
  // Wizard is dormant once all 3 steps are done (we keep the success card around as a celebration moment until dismissed).
  if (hasReceiver && hasStrategy && hasLink && collapsed) return null;

  const allDone = hasReceiver && hasStrategy && hasLink;
  const stepClass = (done, current) => done
    ? "border-emerald-500/30 bg-emerald-500/[0.08]"
    : current
      ? "border-purple-400/40 bg-purple-500/[0.08] ring-1 ring-purple-400/20"
      : "border-white/10 bg-white/[0.02]";

  // Active step = the first not-done one
  const activeStep = !hasReceiver ? 1 : !hasStrategy ? 2 : !hasLink ? 3 : 4;

  return (
    <div className="bx-card mt-3 p-5" data-testid="upi-quick-wizard" style={{ background: allDone ? "linear-gradient(135deg, rgba(16,185,129,0.06), rgba(59,130,246,0.04))" : "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.04))" }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="bx-heading text-base font-bold flex items-center gap-2">
            {allDone ? <Lucide.CheckCircle2 size={18} className="text-emerald-300" /> : <Lucide.Sparkles size={18} className="text-purple-300" />}
            {allDone ? "All set — your UPI Pay Link is live!" : "Quick setup — get your UPI Pay Link live in 60s"}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {allDone
              ? <>Your public page <span className="bx-mono text-purple-300">/UPI</span> is accepting payments. You can keep going with auto-confirm, caps, and analytics from the tabs below.</>
              : "Three quick steps. Each one stays checked as you complete it."}
          </p>
        </div>
        {allDone && (
          <button onClick={() => setCollapsed(true)} className="bx-btn-ghost text-xs" data-testid="wizard-dismiss"><Lucide.X size={12} /> Dismiss</button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-3 mt-5">
        <WizardStep
          n={1}
          done={hasReceiver}
          current={activeStep === 1}
          className={stepClass(hasReceiver, activeStep === 1)}
          title="Add a UPI receiver"
          hint="Add at least one UPI ID where customers will pay. You can add multiple."
          counter={hasReceiver ? `${receivers.length} receiver${receivers.length === 1 ? "" : "s"} added` : "Not added yet"}
          cta={hasReceiver
            ? <button onClick={() => onJumpTab("receivers")} className="bx-btn-ghost text-xs" data-testid="wizard-jump-receivers"><Lucide.Plus size={12} /> Add another</button>
            : <button onClick={onAddReceiver} className="bx-btn-primary text-xs" data-testid="wizard-step1-cta"><Lucide.Plus size={12} /> Add UPI ID</button>}
        />
        <WizardStep
          n={2}
          done={hasStrategy}
          current={activeStep === 2}
          className={stepClass(hasStrategy, activeStep === 2)}
          title="Set up a routing strategy"
          hint="Tells the system which UPI ID gets paid. Start with a single fallback range (₹0–∞ → your UPI)."
          counter={hasStrategy ? `${strategyCount} strateg${strategyCount === 1 ? "y" : "ies"} configured` : "Not set up yet"}
          cta={
            <button
              onClick={() => onJumpTab("strategies")}
              disabled={!hasReceiver}
              className={hasStrategy ? "bx-btn-ghost text-xs" : "bx-btn-primary text-xs"}
              data-testid="wizard-step2-cta"
            >
              {hasStrategy ? <><Lucide.Settings size={12} /> Manage</> : <><Lucide.ArrowRight size={12} /> Go to Strategies</>}
            </button>
          }
        />
        <WizardStep
          n={3}
          done={hasLink}
          current={activeStep === 3}
          className={stepClass(hasLink, activeStep === 3)}
          title="Send a test pay link"
          hint="Create a tiny ₹1 link and pay yourself — confirms the full flow works end-to-end."
          counter={hasLink ? `${linkCount} pay link${linkCount === 1 ? "" : "s"} created` : "Not sent yet"}
          cta={
            <button
              onClick={() => { onJumpTab("links"); onCreateLink(); }}
              disabled={!hasReceiver || !hasStrategy}
              className={hasLink ? "bx-btn-ghost text-xs" : "bx-btn-primary text-xs"}
              data-testid="wizard-step3-cta"
            >
              {hasLink ? <><Lucide.Link2 size={12} /> Create another</> : <><Lucide.Send size={12} /> Send test link</>}
            </button>
          }
        />
      </div>

      {!allDone && (
        <div className="text-[10px] text-slate-500 mt-4 flex items-center gap-2">
          <Lucide.Info size={11} /> Until all three steps are complete, the public <span className="bx-mono text-purple-300">/UPI</span> page will say "unavailable".
        </div>
      )}
    </div>
  );
}

function WizardStep({ n, done, current, className, title, hint, counter, cta }) {
  return (
    <div className={`rounded-xl border p-4 transition ${className}`} data-testid={`wizard-step-${n}`}>
      <div className="flex items-center gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done ? "bg-emerald-500/20 text-emerald-300" : current ? "bg-purple-500/20 text-purple-300 ring-2 ring-purple-400/30" : "bg-white/5 text-slate-400"}`}>
          {done ? <Lucide.Check size={14} /> : n}
        </div>
        <div className="text-sm font-semibold text-slate-100">{title}</div>
      </div>
      <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">{hint}</div>
      <div className={`text-[10px] uppercase tracking-wider mt-3 mb-2 ${done ? "text-emerald-300" : "text-slate-500"}`}>{counter}</div>
      <div>{cta}</div>
    </div>
  );
}

function ExpiringSoonWidget() {
  const [links, setLinks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [horizon, setHorizon] = useState(6);

  const load = (h) => {
    api.get(`/admin/upi/links?expiring_within_hours=${h}`)
      .then((r) => setLinks(r.data))
      .catch(() => setLinks([]))
      .finally(() => setLoaded(true));
  };

  useEffect(() => { load(horizon); /* refresh every minute */
    const t = setInterval(() => load(horizon), 60_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon]);

  const linkUrl = (l) => `${window.location.origin}/pay/${l.token}`;
  const waResend = (l) => {
    const amt = Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 });
    const expIn = Math.max(0, Math.round((new Date(l.expires_at).getTime() - Date.now()) / 60000));
    const txt = `Hi! Just a friendly nudge — your BINAREX AND CO payment link for ₹${amt}${l.label ? ` (${l.label})` : ""} expires in about ${expIn} min. Tap to pay: ${linkUrl(l)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener,noreferrer");
  };

  if (!loaded) return null;
  if (!links.length) return null;

  return (
    <div className="bx-card p-4 mt-3" data-testid="paylinks-expiring-widget" style={{ borderColor: "rgba(245,158,11,0.25)" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <Lucide.Hourglass size={18} className="text-amber-300" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/80">Pay links expiring soon</div>
          <div className="text-xs text-slate-400 mt-0.5">{links.length} active link{links.length === 1 ? "" : "s"} within the next {horizon}h — give the customer a nudge before they expire.</div>
        </div>
        <select value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} className="bx-input text-xs py-1.5 w-28" data-testid="paylinks-expiring-horizon">
          <option value={1}>next 1h</option>
          <option value={3}>next 3h</option>
          <option value={6}>next 6h</option>
          <option value={12}>next 12h</option>
          <option value={24}>next 24h</option>
        </select>
      </div>
      <div className="mt-3 space-y-2">
        {links.map((l) => {
          const expIn = Math.max(0, Math.round((new Date(l.expires_at).getTime() - Date.now()) / 60000));
          const urgent = expIn < 60;
          return (
            <div key={l.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`paylinks-expiring-row-${l.id.slice(0, 8)}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-100 truncate">{l.label || "(no label)"}</span>
                  <span className="bx-mono text-xs text-purple-300">₹{Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                  <Lucide.Clock size={10} />
                  <span className={urgent ? "text-amber-300 font-semibold" : ""}>
                    expires in {expIn < 60 ? `${expIn} min` : `${Math.floor(expIn / 60)}h ${expIn % 60}m`}
                  </span>
                  <span>·</span>
                  <span>{l.taps_count || 0} tap{(l.taps_count || 0) === 1 ? "" : "s"}</span>
                  {l.customer_mobile_hint && <><span>·</span><span className="bx-mono">{l.customer_mobile_hint}</span></>}
                </div>
              </div>
              <button onClick={() => waResend(l)} className="bx-btn-ghost text-xs text-emerald-300" title="Resend via WhatsApp" data-testid={`paylinks-expiring-wa-${l.id.slice(0, 8)}`}>
                <Lucide.MessageCircle size={12} /> Resend
              </button>
              <button onClick={() => { navigator.clipboard.writeText(linkUrl(l)); toast.success("Link copied"); }} className="bx-btn-ghost text-xs" title="Copy link">
                <Lucide.Copy size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PayLinksPanel({ links, onReload, onOpenEmail }) {
  const publicBase = (typeof window !== "undefined" ? window.location.origin : "");

  const linkUrl = (l) => `${publicBase}/pay/${l.token}`;

  const copy = async (l) => {
    try {
      await navigator.clipboard.writeText(linkUrl(l));
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const waShare = (l) => {
    const amt = Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 });
    const txt = `Hi! Here is your secure UPI payment link from BINAREX AND CO for ₹${amt}${l.label ? ` (${l.label})` : ""} — ${linkUrl(l)}\n\nIt opens directly in your UPI app. Valid for 24 hours.`;
    const url = `https://wa.me/?text=${encodeURIComponent(txt)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const smsShare = (l) => {
    const amt = Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 });
    const body = `Pay ₹${amt} via BINAREX UPI: ${linkUrl(l)} (24h, single-use)`;
    window.location.href = `sms:?body=${encodeURIComponent(body)}`;
  };

  const revoke = async (l) => {
    if (!window.confirm(`Revoke pay link for ₹${l.amount}? Customer will see "Link revoked".`)) return;
    try {
      await api.delete(`/admin/upi/links/${l.id}`);
      toast.success("Link revoked");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bx-card mt-4 overflow-x-auto" data-testid="paylinks-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className="text-left p-3">Created</th>
            <th className="text-left p-3">Label</th>
            <th className="text-right p-3">Amount</th>
            <th className="text-left p-3">Status</th>
            <th className="text-center p-3" title="Number of times the link was viewed">Taps</th>
            <th className="text-left p-3">Expires</th>
            <th className="text-left p-3">Link</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {links.map((l) => {
            const eff = l.effective_status || l.status || "active";
            const meta = PAY_LINK_STATUS_BADGE(eff);
            const Icon = meta.icon;
            const isActive = eff === "active";
            const taps = Number(l.taps_count || 0);
            const isPaid = eff === "paid";
            const lastTapAt = l.last_tap_at ? new Date(l.last_tap_at).toLocaleString() : "never";
            return (
              <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`paylink-row-${l.id.slice(0, 8)}`}>
                <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="p-3 text-slate-300 max-w-[220px] truncate">{l.label || <span className="text-slate-600">—</span>}</td>
                <td className="p-3 text-right font-bold text-purple-300">₹{Number(l.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.color}`}>
                    <Icon size={10} /> {eff}
                  </span>
                </td>
                <td className="p-3 text-center" data-testid={`paylink-taps-${l.id.slice(0, 8)}`} title={`Last tap: ${lastTapAt}${l.last_tap_ip ? ` · IP ${l.last_tap_ip}` : ""}${l.last_tap_ua ? ` · ${l.last_tap_ua.slice(0, 60)}` : ""}`}>
                  <div className="text-sm text-slate-200 font-semibold">{taps}</div>
                  {taps > 0 && (
                    <div className={`text-[9px] uppercase tracking-wider ${isPaid ? "text-emerald-300" : "text-slate-500"}`}>
                      {isPaid ? "converted" : `${l.last_tap_at ? "tapped" : "—"}`}
                    </div>
                  )}
                </td>
                <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{new Date(l.expires_at).toLocaleString()}</td>
                <td className="p-3 bx-mono text-[11px] text-purple-300/90 max-w-[160px] truncate">/pay/{l.token}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`paylink-copy-${l.id.slice(0, 8)}`} onClick={() => copy(l)} className="bx-btn-ghost text-xs" title="Copy link"><Lucide.Copy size={12} /></button>
                  {isActive && <button data-testid={`paylink-email-${l.id.slice(0, 8)}`} onClick={() => onOpenEmail(l)} className="bx-btn-ghost text-xs ml-1" title="Email to customer"><Lucide.Mail size={12} /></button>}
                  {isActive && <button data-testid={`paylink-wa-${l.id.slice(0, 8)}`} onClick={() => waShare(l)} className="bx-btn-ghost text-xs ml-1 text-emerald-300" title="Share via WhatsApp"><Lucide.MessageCircle size={12} /></button>}
                  {isActive && <button data-testid={`paylink-sms-${l.id.slice(0, 8)}`} onClick={() => smsShare(l)} className="bx-btn-ghost text-xs ml-1" title="Share via SMS"><Lucide.MessageSquare size={12} /></button>}
                  <a href={linkUrl(l)} target="_blank" rel="noopener noreferrer" className="bx-btn-ghost text-xs ml-1" title="Open in new tab"><Lucide.ExternalLink size={12} /></a>
                  {isActive && <button data-testid={`paylink-revoke-${l.id.slice(0, 8)}`} onClick={() => revoke(l)} className="bx-btn-ghost text-xs ml-1 text-rose-300" title="Revoke"><Lucide.Trash2 size={12} /></button>}
                </td>
              </tr>
            );
          })}
          {!links.length && <tr><td colSpan={8} className="p-12 text-center text-slate-500 text-sm">No payment links yet. Click <strong>Create pay link</strong> to generate one.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
